import { Subject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpHeaders } from '@angular/common/http';
import { IRequestAggregatorItemResponse } from '@nimbus-global/features/shared/models/api/request-aggregator/i-request-aggregator-item.response';
import { IRequestAggregatorItemRequest } from '@nimbus-global/features/shared/models/api/request-aggregator/i-request-aggregator-item.request';
import { RequestAggregatorMethod } from '@nimbus-global/features/shared/models/api/request-aggregator/request-aggregator-method.enum';
import { QUERY_PARAM_CONFIGS } from '@nimbus-library/features/shared/models/api/query-params-configuration.model';

export class AggregatedRequestScope {
  private requestFinished$: Subject<IRequestAggregatorItemResponse[]>;
  private requests$: IRequestAggregatorItemRequest[];

  public constructor() {
    this.requestFinished$ = new Subject<IRequestAggregatorItemResponse[]>();
    this.requests$ = [];
  }

  public addRequest$<TResult>(
    method: RequestAggregatorMethod,
    url: string,
    body?: string,
    headers?: Record<string, string>,
  ): Observable<TResult> {
    const index = this.requests$.length;
    const urlWithParams = this.appendQueryParams(url);
    this.requests$.push({ method, url: urlWithParams, body, headers });

    return this.requestFinished$.pipe(
      map((response) => response[index]),
      map((respItem) => {
        if (respItem.statusCode >= 200 && respItem.statusCode < 300) {
          return respItem.body ? JSON.parse(respItem.body) : null;
        }

        throw {
          url: urlWithParams,
          status: respItem.statusCode,
          statusText: respItem.body,
          error: respItem.body,
          headers: new HttpHeaders(),
        };
      }),
    );
  }

  public execute(executor: (request: IRequestAggregatorItemRequest[]) => Observable<IRequestAggregatorItemResponse[]>) {
    executor(this.requests$).subscribe({
      next: (response) => this.requestFinished$.next(response),
      error: (error) => this.requestFinished$.error(error),
      complete: () => this.requestFinished$.complete(),
    });
  }

  private appendQueryParams(url: string): string {
    if (!url) {
      return url;
    }

    let updatedUrl = url;

    for (const { urlSegment, queryParam, queryValue } of QUERY_PARAM_CONFIGS) {
      const segmentVariants = [urlSegment, urlSegment.replace(/\/$/, '')];
      const matchesSegment = segmentVariants.some((seg) => seg && updatedUrl.includes(seg));
      if (!matchesSegment) {
        continue;
      }

      const alreadyPresent = new RegExp(`[?&]${queryParam}=`, 'i').test(updatedUrl);
      if (alreadyPresent) {
        continue;
      }

      const separator = updatedUrl.includes('?') ? '&' : '?';
      updatedUrl = `${updatedUrl}${separator}${queryParam}=${encodeURIComponent(queryValue)}`;
    }

    return updatedUrl;
  }
}
